<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useProposalsStore } from 'dashboard/stores/proposals';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  selectedContact: { type: Object, required: true },
});

const emit = defineEmits(['create']);

const { t } = useI18n();
const store = useProposalsStore();

// Fetched once by the parent (ContactManageView) for this contact - not
// re-fetched here to avoid a redundant duplicate request alongside
// ContactProposals, which reads the same store.
const latestProposal = computed(() => {
  const contactProposals = store.records
    .filter(p => p.contact_id === props.selectedContact.id)
    .sort((a, b) => b.id - a.id);
  return contactProposals[0] || null;
});

const deliveryLabel = state =>
  t(`PROPOSALS_MGMT.DELIVERY_STATE.${state.toUpperCase()}`);
</script>

<template>
  <div class="flex flex-col gap-2">
    <div
      v-if="latestProposal"
      class="flex flex-col gap-1 p-3 text-sm border rounded-lg border-n-weak bg-n-solid-1"
    >
      <div class="flex items-center justify-between">
        <span class="text-xs font-medium uppercase text-n-slate-11">{{
          t('PROPOSALS_MGMT.CONTACT_SIDEBAR.LATEST_PROPOSAL')
        }}</span>
        <span
          class="px-2 py-0.5 text-xs rounded-full whitespace-nowrap"
          :class="
            latestProposal.delivery_state === 'sent'
              ? 'bg-n-teal-3 text-n-teal-11'
              : 'bg-n-slate-3 text-n-slate-11'
          "
        >
          {{ deliveryLabel(latestProposal.delivery_state) }}
        </span>
      </div>
      <span class="font-medium text-n-slate-12">{{
        latestProposal.reference
      }}</span>
      <div class="flex items-center gap-2 mt-1">
        <a
          v-if="latestProposal.pdf_url"
          :href="latestProposal.pdf_url"
          target="_blank"
          rel="noopener noreferrer"
          class="px-2 py-1 text-xs font-medium border rounded-lg border-n-weak text-n-slate-12 hover:bg-n-slate-3"
        >
          {{ t('PROPOSALS_MGMT.CONTACT_SIDEBAR.VIEW_PROPOSAL') }}
        </a>
        <Button
          :label="t('PROPOSALS_MGMT.CONTACT_SIDEBAR.CREATE_NEW')"
          size="sm"
          variant="ghost"
          @click="emit('create')"
        />
      </div>
    </div>
    <Button
      v-else
      :label="t('PROPOSALS_MGMT.CREATE_PROPOSAL')"
      size="sm"
      class="w-full"
      @click="emit('create')"
    />
  </div>
</template>
